import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setCalendarData, addSchedule, updateSchedule, removeSchedule } from '../store/calendarSlice';
import GravityLogo from './GravityLogo';
import { setProductData } from '../store/productSlice';
import { setAdminData } from '../store/adminSlice';
import { PW_Calendar_API, PW_Products_API, PW_Stores_API, PW_Calendar_Save_API, PW_Calendar_Delete_API } from '../Server/ApiV2';
import Spinner from './Spinner';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function CalendarSettings() {
  const dispatch = useDispatch();
  const { schedules } = useSelector(s => s.calendar);
  const { stores } = useSelector(s => s.admin);
  const { products, productTypes, productTypeLinks } = useSelector(s => s.product);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [calRes, prodRes, storeRes] = await Promise.all([
        PW_Calendar_API(), PW_Products_API(), PW_Stores_API(),
      ]);
      if (cancelled) return;
      if (calRes?.data?.success) dispatch(setCalendarData(calRes.data.data));
      if (prodRes?.data?.success) dispatch(setProductData(prodRes.data.data));
      if (storeRes?.data?.success) dispatch(setAdminData({ stores: storeRes.data.data }));
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  const activeStores = stores.filter(s => s.status === 'Active');
  const activeProducts = products.filter(p => p.status === 'Active');
  const activeTypes = productTypes.filter(t => t.status === 'Active');

  const emptyForm = {
    dateMode: 'day',
    startDate: '',
    endDate: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    weekdays: [],
    storeIds: [],
    productIds: [],
    productTypeOrder: [],
  };

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [viewStore, setViewStore] = useState(activeStores[0]?.id || '');
  const [viewProduct, setViewProduct] = useState(activeProducts[0]?.id || '');
  const [viewMonth, setViewMonth] = useState(new Date().getMonth() + 1);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getSchedulesForDay = (storeId, productId, dateStr) => {
    const d = new Date(dateStr + 'T00:00');
    const m = d.getMonth() + 1;
    const y = d.getFullYear();
    const dow = d.getDay();
    const active = schedules.filter(s => s.status === 'Active' && s.storeIds.includes(storeId)
      && (!productId || !s.productIds?.length || s.productIds.includes(productId)));
    const day = active.filter(s => s.dateMode === 'day' && s.startDate === dateStr);
    if (day.length) return day;
    const wk = active.filter(s => s.dateMode === 'weekday' && (s.weekdays || []).includes(dow));
    if (wk.length) return wk;
    const rng = active.filter(s => s.dateMode === 'range' && dateStr >= s.startDate && dateStr <= s.endDate);
    if (rng.length) return rng;
    return active.filter(s => s.dateMode === 'month' && s.month === m && s.year === y);
  };

  const viewLinkedTypes = viewProduct
    ? productTypeLinks.filter(l => l.linked && l.productId === viewProduct).map(l => l.typeId)
    : null;

  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
  const viewDays = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    const dateStr = `${viewYear}-${String(viewMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dateObj = new Date(dateStr + 'T00:00');
    const dayName = DAYS[dateObj.getDay()];
    const daySchedules = viewStore ? getSchedulesForDay(viewStore, viewProduct, dateStr) : [];
    return { day, dateStr, dayName, schedules: daySchedules };
  });

  const linkedTypeIds = form.productIds.length > 0
    ? [...new Set(productTypeLinks.filter(l => l.linked && form.productIds.includes(l.productId)).map(l => l.typeId))]
    : activeTypes.map(t => t.id);
  const availableTypes = activeTypes.filter(t => linkedTypeIds.includes(t.id));

  const isValid = form.storeIds.length > 0 && form.productIds.length > 0 && form.productTypeOrder.length > 0 && (
    form.dateMode === 'month' ||
    (form.dateMode === 'weekday' && form.weekdays.length > 0) ||
    (form.dateMode === 'day' && form.startDate) ||
    (form.dateMode === 'range' && form.startDate && form.endDate)
  );

  const resetForm = () => {
    setForm(emptyForm); setEditing(null); setFormError('');
  };

  const handleToggleStore = (storeId) => {
    setForm(prev => ({
      ...prev,
      storeIds: prev.storeIds.includes(storeId)
        ? prev.storeIds.filter(id => id !== storeId)
        : [...prev.storeIds, storeId],
    }));
  };

  const handleSelectProduct = (productId) => {
    setForm(prev => {
      const newIds = prev.productIds.includes(productId) ? [] : [productId];
      const linkedTypes = newIds.length > 0
        ? productTypeLinks.filter(l => l.linked && newIds.includes(l.productId)).map(l => l.typeId)
        : [];
      return { ...prev, productIds: newIds, productTypeOrder: linkedTypes };
    });
  };

  const handleAddType = (typeId) => {
    setForm(prev => ({ ...prev, productTypeOrder: [...prev.productTypeOrder, typeId] }));
  };

  const handleRemoveType = (index) => {
    setForm(prev => ({ ...prev, productTypeOrder: prev.productTypeOrder.filter((_, i) => i !== index) }));
  };

  const handleMoveType = (index, direction) => {
    setForm(prev => {
      const order = [...prev.productTypeOrder];
      const target = index + direction;
      if (target < 0 || target >= order.length) return prev;
      [order[index], order[target]] = [order[target], order[index]];
      return { ...prev, productTypeOrder: order };
    });
  };

  const handleSave = () => {
    if (!isValid) return;
    setFormError('');
    const conflict = schedules.find(s => {
      if (editing && s.id === editing) return false;
      if (s.dateMode !== form.dateMode) return false;
      if (!s.storeIds.some(sid => form.storeIds.includes(sid))) return false;
      if (form.productIds.length && s.productIds?.length && !s.productIds.some(pid => form.productIds.includes(pid))) return false;
      if (form.dateMode === 'day') return s.startDate === form.startDate;
      if (form.dateMode === 'weekday') return (s.weekdays || []).some(d => form.weekdays.includes(d));
      if (form.dateMode === 'range') return form.startDate <= s.endDate && form.endDate >= s.startDate;
      if (form.dateMode === 'month') return s.month === form.month && s.year === form.year;
      return false;
    });
    if (conflict) {
      dispatch(removeSchedule(conflict.id));
      PW_Calendar_Delete_API(conflict.id).catch(err => console.error('[Calendar] Conflict delete failed:', err));
    }
    if (editing) {
      const schedData = { id: editing, ...form };
      dispatch(updateSchedule(schedData));
      PW_Calendar_Save_API(schedData).catch(err => console.error('[Calendar] Update failed:', err));
    } else {
      const schedData = { id: `CAL-${Date.now()}`, ...form, status: 'Active', createdAt: new Date().toISOString() };
      dispatch(addSchedule(schedData));
      PW_Calendar_Save_API(schedData).catch(err => console.error('[Calendar] Save failed:', err));
    }
    resetForm();
  };

  const handleEdit = (sched) => {
    setEditing(sched.id);
    setForm({
      dateMode: sched.dateMode,
      startDate: sched.startDate || '',
      endDate: sched.endDate || '',
      month: sched.month || new Date().getMonth() + 1,
      year: sched.year || new Date().getFullYear(),
      weekdays: [...(sched.weekdays || [])],
      storeIds: [...sched.storeIds],
      productIds: [...(sched.productIds || [])],
      productTypeOrder: [...sched.productTypeOrder],
    });
  };

  const formatDate = (sched) => {
    const toDate = (v) => new Date(typeof v === 'string' && v.includes('T') ? v : v + 'T00:00');
    if (sched.dateMode === 'weekday') {
      return (sched.weekdays || []).map(d => DAYS[d]).join(', ');
    }
    if (sched.dateMode === 'day') {
      return toDate(sched.startDate).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    }
    if (sched.dateMode === 'range') {
      const s = toDate(sched.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const e = toDate(sched.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${s} — ${e}`;
    }
    return `${MONTHS[sched.month - 1]} ${sched.year}`;
  };

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Calendar Settings</h2>
          <div className="page-header-sub">Schedule product types for stores by date</div>
        </div>
      </div>

      <div className="card mb-24">
        <div className="card-title gold">Store Schedule View</div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 16 }}>
            <div style={{ flex: 1, maxWidth: 220 }}>
              <div className="text-sm text-muted mb-8">Store</div>
              <select className="form-select" value={viewStore}
                onChange={e => setViewStore(e.target.value)}>
                {activeStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, maxWidth: 180 }}>
              <div className="text-sm text-muted mb-8">Product</div>
              <select className="form-select" value={viewProduct}
                onChange={e => setViewProduct(e.target.value)}>
                {activeProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-sm text-muted mb-8">Month</div>
              <select className="form-select" value={viewMonth}
                onChange={e => setViewMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <div className="text-sm text-muted mb-8">Year</div>
              <select className="form-select" value={viewYear}
                onChange={e => setViewYear(Number(e.target.value))}>
                {[2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          <div className="table-wrap">
            <table className="data-table cal-view-table">
              <thead>
                <tr>
                  <th style={{ width: 46 }}>Date</th>
                  <th style={{ width: 40 }}>Day</th>
                  <th>Product Types</th>
                  <th style={{ width: 90 }}>Source</th>
                </tr>
              </thead>
              <tbody>
                {viewDays.map(({ day, dayName, schedules: dayScheds }) => {
                  const isWeekend = dayName === 'Sat' || dayName === 'Sun';
                  return (
                    <tr key={day} className={isWeekend ? 'cal-weekend' : ''}>
                      <td className="fw-600">{day}</td>
                      <td className={isWeekend ? 'fw-600' : 'text-muted'}>{dayName}</td>
                      <td>
                        {dayScheds.length > 0 ? (
                          dayScheds.map((sched, si) => {
                            const types = sched.productTypeOrder.filter(tid => !viewLinkedTypes || viewLinkedTypes.includes(tid));
                            return (
                              <div key={sched.id} style={{ marginBottom: si < dayScheds.length - 1 ? 4 : 0 }}>
                                {types.map((tid, i) => (
                                  <React.Fragment key={tid}>
                                    {i > 0 && <span className="cal-arrow">→</span>}
                                    <span className="chip chip-purple" style={{ fontSize: 11 }}>{tid}</span>
                                  </React.Fragment>
                                ))}
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {dayScheds.length > 0 && dayScheds.map((sched, si) => (
                          <div key={sched.id} style={{ marginBottom: si < dayScheds.length - 1 ? 4 : 0 }}>
                            <span className={`chip chip-${sched.dateMode === 'weekday' ? 'blue' : sched.dateMode === 'day' ? 'green' : sched.dateMode === 'range' ? 'purple' : 'gold'}`} style={{ fontSize: 10 }}>
                              {sched.dateMode === 'weekday' ? 'Wkday' : sched.dateMode === 'day' ? 'Day' : sched.dateMode === 'range' ? 'Range' : 'Month'}
                            </span>
                          </div>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card mb-24">
        <div className="card-title purple">{editing ? 'Edit Schedule' : 'New Schedule'}</div>
        <div className="card-body">

          <div className="cal-section">
            <div className="cal-label">Date Selection</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {[['day', 'Day'], ['weekday', 'Week Day'], ['range', 'Date Range'], ['month', 'Month']].map(([mode, label]) => (
                <button key={mode}
                  className={`btn btn-sm ${form.dateMode === mode ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setForm(prev => ({ ...prev, dateMode: mode }))}>
                  {label}
                </button>
              ))}
            </div>

            {form.dateMode === 'weekday' && (
              <div>
                <div className="text-sm text-muted mb-8">Select Days</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {DAYS.map((dayName, idx) => (
                    <label key={idx} className={`cal-store-check${form.weekdays.includes(idx) ? ' selected' : ''}`} style={{ minWidth: 80 }}>
                      <input type="checkbox"
                        checked={form.weekdays.includes(idx)}
                        onChange={() => setForm(prev => ({
                          ...prev,
                          weekdays: prev.weekdays.includes(idx)
                            ? prev.weekdays.filter(d => d !== idx)
                            : [...prev.weekdays, idx].sort((a, b) => a - b),
                        }))} />
                      <span>{dayName}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {form.dateMode === 'day' && (
              <div>
                <div className="text-sm text-muted mb-8">Date</div>
                <input type="date" className="form-input" value={form.startDate}
                  onChange={e => setForm(prev => ({ ...prev, startDate: e.target.value }))}
                  style={{ width: 200 }} />
              </div>
            )}
            {form.dateMode === 'range' && (
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                <div>
                  <div className="text-sm text-muted mb-8">From</div>
                  <input type="date" className="form-input" value={form.startDate}
                    onChange={e => setForm(prev => ({ ...prev, startDate: e.target.value }))}
                    style={{ width: 200 }} />
                </div>
                <div>
                  <div className="text-sm text-muted mb-8">To</div>
                  <input type="date" className="form-input" value={form.endDate}
                    onChange={e => setForm(prev => ({ ...prev, endDate: e.target.value }))}
                    style={{ width: 200 }} />
                </div>
              </div>
            )}
            {form.dateMode === 'month' && (
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                <div>
                  <div className="text-sm text-muted mb-8">Month</div>
                  <select className="form-select" value={form.month}
                    onChange={e => setForm(prev => ({ ...prev, month: Number(e.target.value) }))}>
                    {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-sm text-muted mb-8">Year</div>
                  <select className="form-select" value={form.year}
                    onChange={e => setForm(prev => ({ ...prev, year: Number(e.target.value) }))}>
                    {[2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="cal-section">
            <div className="cal-label">Stores</div>
            <div className="cal-store-grid">
              {activeStores.map(store => (
                <label key={store.id} className={`cal-store-check${form.storeIds.includes(store.id) ? ' selected' : ''}`}>
                  <input type="checkbox"
                    checked={form.storeIds.includes(store.id)}
                    onChange={() => handleToggleStore(store.id)} />
                  <span>{store.name}</span>
                  <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>{store.code}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="cal-section">
            <div className="cal-label">Product</div>
            <div className="cal-store-grid">
              {activeProducts.map(p => (
                <label key={p.id} className={`cal-store-check${form.productIds.includes(p.id) ? ' selected' : ''}`}>
                  <input type="radio" name="cal-product"
                    checked={form.productIds.includes(p.id)}
                    onChange={() => handleSelectProduct(p.id)} />
                  <span>{p.name}</span>
                  <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>RSP {p.rsp}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="cal-section">
            <div className="cal-label">Product Type Hierarchy</div>
            <div className="text-sm text-muted mb-8">Click types to add in priority order:</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {availableTypes.map(t => (
                <button key={t.id} className="btn btn-outline btn-sm" onClick={() => handleAddType(t.id)}>
                  + {t.name}
                </button>
              ))}
            </div>

            {form.productTypeOrder.length > 0 && (() => {
              const prod = products.find(pr => form.productIds.includes(pr.id));
              const factor = prod?.priceAmount || 1;
              const scale = prod ? 100 / prod.cardsPerBundle : 1;
              const isAny2 = prod && (prod.matchingMethod || prod.winCondition) === 'any2';
              const selling = prod ? prod.rsp * 100 : 0;
              let grandPrize = 0;
              const typeStats = form.productTypeOrder.map(typeId => {
                const link = productTypeLinks.find(l => l.productId === prod?.id && l.typeId === typeId && l.linked);
                const config = link?.winnerConfig || {};
                const winners = Object.values(config).reduce((s, v) => s + (v || 0), 0);
                const configPrize = Object.entries(config).reduce((s, [d, c]) => s + Number(d) * (c || 0), 0) * factor;
                const scaledPrize = Math.round(configPrize * scale);
                const scaledWinners = Math.round(winners * scale);
                const minGuarantee = isAny2 ? (100 - scaledWinners) * 20 * factor : 0;
                const totalPrize = scaledPrize + minGuarantee;
                grandPrize += totalPrize;
                return { typeId, totalPrize, configPrize: scaledPrize, minGuarantee };
              });
              const grandPnL = selling * form.productTypeOrder.length - grandPrize;
              return (
              <div>
                <div className="cal-type-order">
                  {form.productTypeOrder.map((typeId, idx) => {
                    const type = activeTypes.find(t => t.id === typeId);
                    const stats = typeStats[idx];
                    return (
                      <div key={`${typeId}-${idx}`} className="cal-type-item" style={{ flexWrap: 'wrap' }}>
                        <span className="cal-type-num">{idx + 1}</span>
                        <span className="fw-600">{type?.name || typeId}</span>
                        <span className="text-sm text-muted">{type?.label || ''}</span>
                        <div style={{ display: 'flex', gap: 12, marginLeft: 12 }}>
                          <span className="text-sm" style={{ color: '#2d1b69' }}>Sell: <strong>{selling.toLocaleString()}</strong></span>
                          <span className="text-sm" style={{ color: '#c9a227' }}>Prize: <strong>{stats.totalPrize.toLocaleString()}</strong></span>
                          <span className="text-sm" style={{ color: (selling - stats.totalPrize) >= 0 ? '#00895e' : '#e74c3c' }}>
                            P&L: <strong>{(selling - stats.totalPrize).toLocaleString()}</strong>
                          </span>
                        </div>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                          <button className="btn btn-sm btn-secondary cal-move-btn"
                            disabled={idx === 0} onClick={() => handleMoveType(idx, -1)}>↑</button>
                          <button className="btn btn-sm btn-secondary cal-move-btn"
                            disabled={idx === form.productTypeOrder.length - 1} onClick={() => handleMoveType(idx, 1)}>↓</button>
                          <button className="btn btn-sm cal-remove-btn"
                            onClick={() => handleRemoveType(idx)}>×</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {prod && form.productTypeOrder.length > 0 && (
                  <div style={{ display: 'flex', gap: 16, marginTop: 8, padding: '8px 12px', background: '#f5f3fa', borderRadius: 6, fontSize: 13 }}>
                    <span className="fw-600" style={{ color: '#2d1b69' }}>
                      Total ({form.productTypeOrder.length} types × 100):
                    </span>
                    <span style={{ color: '#2d1b69' }}>Sell: <strong>{(selling * form.productTypeOrder.length).toLocaleString()}</strong></span>
                    <span style={{ color: '#c9a227' }}>Prize: <strong>{grandPrize.toLocaleString()}</strong></span>
                    <span style={{ color: grandPnL >= 0 ? '#00895e' : '#e74c3c' }}>P&L: <strong>{grandPnL.toLocaleString()}</strong></span>
                  </div>
                )}
              </div>
              );
            })()}
          </div>

          {formError && (
            <div style={{ color: '#c62828', background: '#fef2f2', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginTop: 8 }}>
              {formError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-success" disabled={!isValid} onClick={handleSave}>
              {editing ? 'Update Schedule' : 'Save Schedule'}
            </button>
            {editing && <button className="btn btn-secondary" onClick={resetForm}>Cancel</button>}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Saved Schedules ({schedules.length})</div>
        <div className="card-body">
          {schedules.length === 0 ? (
            <div className="empty-state"><GravityLogo size={48} circle /><p>No schedules configured yet</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {schedules.map(sched => (
                <div key={sched.id} className="cal-schedule-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <span className="fw-700" style={{ fontSize: 15, color: '#2d1b69' }}>{formatDate(sched)}</span>
                      <span className={`chip chip-${sched.dateMode === 'weekday' ? 'blue' : 'purple'}`} style={{ marginLeft: 8 }}>
                        {sched.dateMode === 'weekday' ? 'Weekday' : sched.dateMode === 'day' ? 'Day' : sched.dateMode === 'range' ? 'Range' : 'Month'}
                      </span>
                    </div>
                    <span className={`chip ${sched.status === 'Active' ? 'chip-green' : 'chip-red'}`}>{sched.status}</span>
                  </div>
                  <div className="mb-8">
                    <span className="text-sm text-muted">Stores: </span>
                    <span className="text-sm fw-600">
                      {sched.storeIds.map(sid => stores.find(s => s.id === sid)?.name || sid).join(', ')}
                    </span>
                  </div>
                  <div className="mb-8">
                    <span className="text-sm text-muted">Products: </span>
                    {(sched.productIds || []).map((pid, i) => (
                      <React.Fragment key={pid}>
                        {i > 0 && <span style={{ margin: '0 4px' }}>·</span>}
                        <span className="text-sm fw-600">{products.find(p => p.id === pid)?.name || pid}</span>
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="mb-8">
                    <span className="text-sm text-muted">Type Hierarchy: </span>
                    {sched.productTypeOrder.map((tid, i) => (
                      <React.Fragment key={tid}>
                        {i > 0 && <span className="cal-arrow">→</span>}
                        <span className="chip chip-purple" style={{ fontSize: 11 }}>{tid}</span>
                      </React.Fragment>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => handleEdit(sched)}>Edit</button>
                    <button className="btn btn-sm cal-remove-btn" onClick={() => {
                      dispatch(removeSchedule(sched.id));
                      PW_Calendar_Delete_API(sched.id).catch(err => console.error('[Calendar] Delete failed:', err));
                    }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
